/**
 * A utility class for generating secure passwords that meet common complexity requirements.
 * Passwords generated by this class contain at least one uppercase letter, one lowercase letter,
 * one digit, and one special character.
 */
export class PasswordGenerator
{
    private static readonly MIN_LENGTH = 10;
    private static readonly DEFAULT_LENGTH = 12;

    private static readonly UPPERCASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    private static readonly LOWERCASE_CHARS = 'abcdefghijklmnopqrstuvwxyz';
    private static readonly DIGIT_CHARS = '0123456789';
    private static readonly SPECIAL_CHARS = '!@#$%^&*()-_+=[]{}|;:,.<>/?';

    private static readonly ALL_CHARS =
        PasswordGenerator.UPPERCASE_CHARS +
        PasswordGenerator.LOWERCASE_CHARS +
        PasswordGenerator.DIGIT_CHARS +
        PasswordGenerator.SPECIAL_CHARS;

    private static readonly _randomUint32Buffer = new Uint32Array(1);

    /**
     * Generates a secure password containing at least one uppercase, lowercase, digit, and special character.
     * @param length minimum 10 characters (default is 12)
     */
    public static generatePassword(length: number = PasswordGenerator.DEFAULT_LENGTH): string
    {
        const finalLength = Math.max(length, PasswordGenerator.MIN_LENGTH);

        //Ensure at least one char from each required group
        const passwordChars: string[] = [
            PasswordGenerator.UPPERCASE_CHARS[PasswordGenerator._getRandomIndex(PasswordGenerator.UPPERCASE_CHARS.length)],
            PasswordGenerator.LOWERCASE_CHARS[PasswordGenerator._getRandomIndex(PasswordGenerator.LOWERCASE_CHARS.length)],
            PasswordGenerator.DIGIT_CHARS[PasswordGenerator._getRandomIndex(PasswordGenerator.DIGIT_CHARS.length)],
            PasswordGenerator.SPECIAL_CHARS[PasswordGenerator._getRandomIndex(PasswordGenerator.SPECIAL_CHARS.length)]
        ];

        //Fill remaining characters randomly from all allowed characters
        for (let i = passwordChars.length; i < finalLength; i++)
        {
            passwordChars.push(
                PasswordGenerator.ALL_CHARS[PasswordGenerator._getRandomIndex(PasswordGenerator.ALL_CHARS.length)]
            );
        }

        //Shuffle the characters to avoid predictable positions
        for (let i = passwordChars.length - 1; i > 0; i--)
        {
            const j = PasswordGenerator._getRandomIndex(i + 1);
            [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
        }

        return passwordChars.join('');
    }

    /**
     * Generates a random index number within the range of 0 (inclusive) and the specified maximum value (exclusive).
     *
     * @param {number} max - the upper bound (exclusive) for the random index. Must be a positive number.
     *
     * @return {number} a random integer between 0 (inclusive) and max (exclusive)
     * @throws {Error} if the max parameter is less than or equal to 0
     */
    private static _getRandomIndex(max: number): number
    {
        if (max <= 0)
        {
            throw new Error('Max must be positive.');
        }

        if (typeof crypto === 'undefined' || !crypto.getRandomValues)
        {
            return Math.floor(Math.random() * max);
        }

        crypto.getRandomValues(PasswordGenerator._randomUint32Buffer);
        return PasswordGenerator._randomUint32Buffer[0] % max;
    }
}